/**
 * FilmStorageQRSection — QR label sheets for DTF film storage slots.
 *
 * Sibling of BinQRSection: prints a QR label for every pigeon hole + shelf at
 * the selected location. Each label encodes `THP://HOLE:<label>`, the token the
 * Film Storage station routes to slot-inspect (holes AND shelves resolve by
 * label). Lazy query — QRs are only generated when the preview dialog opens
 * (the dialog shows a spinner during that fetch via the `loading` prop).
 *
 * UX mirrors BinQRSection: large touch targets, location-scoped, ARIA labels —
 * but fully i18n'd (Thread Lab is a zh go-live surface).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useLocation } from '@/contexts/LocationContext';
import { trpc } from '@/lib/trpc';
import { QRPreviewDialog } from './QRPrintLayout';
import { Archive, Info, Loader2, MapPin, QrCode } from 'lucide-react';

export function FilmStorageQRSection() {
  const { t } = useTranslation('settings');
  const { selectedLocation } = useLocation();
  const [dialogOpen, setDialogOpen] = useState(false);

  const locationId = selectedLocation?.id ?? '';

  // Slot count / occupancy for the summary (cheap; always on when a location is set).
  const storageQuery = trpc.filmStorage.getFilmStorage.useQuery(
    { locationId },
    { enabled: !!locationId },
  );

  // QR sheet — lazy, only when the preview dialog opens.
  const qrQuery = trpc.filmStorage.generateFilmSlotQRs.useQuery(
    { locationId },
    { enabled: dialogOpen && !!locationId },
  );

  if (!selectedLocation?.id) {
    return (
      <div
        className="flex flex-col items-center justify-center py-8 text-center"
        role="status"
        aria-label={t('production.filmStorageQr.selectLocation')}
      >
        <MapPin className="h-10 w-10 text-muted-foreground/50 mb-3" aria-hidden="true" />
        <p className="text-muted-foreground mb-2">{t('production.filmStorageQr.selectLocation')}</p>
      </div>
    );
  }

  const slotCount = storageQuery.data?.storage.length ?? 0;
  const filmsStored = storageQuery.data?.occupancy.filmsStored ?? 0;
  const isLoading = storageQuery.isPending;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8" role="status" aria-busy="true">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
        <span className="ml-2 text-muted-foreground">{t('production.filmStorageQr.loading')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg"
        role="region"
        aria-label={t('production.filmStorageQr.sectionTitle')}
      >
        <div className="space-y-1 text-center sm:text-left">
          <p className="text-sm text-muted-foreground">{t('production.filmStorageQr.storageSlots')}</p>
          <p className="text-3xl sm:text-2xl font-semibold">{slotCount}</p>
        </div>
        <div className="space-y-1 text-center sm:text-left">
          <p className="text-sm text-muted-foreground">{t('production.filmStorageQr.filmsStored')}</p>
          <p className="text-3xl sm:text-2xl font-semibold">{filmsStored}</p>
        </div>
      </div>

      {/* Print all slot QRs */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Archive className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Label className="text-base font-medium">{t('production.filmStorageQr.sectionTitle')}</Label>
        </div>
        <p className="text-sm text-muted-foreground">
          {t('production.filmStorageQr.description', { count: slotCount })}
        </p>
        <QRPreviewDialog
          qrCodes={qrQuery.data ?? []}
          binType="FILM_SLOT"
          loading={qrQuery.isFetching}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
        >
          <Button
            variant="outline"
            className="h-12 gap-2"
            disabled={slotCount === 0 || qrQuery.isFetching}
            aria-label={t('production.filmStorageQr.printButton')}
            aria-busy={qrQuery.isFetching}
          >
            {qrQuery.isFetching ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                <span>{t('production.filmStorageQr.loadingButton')}</span>
              </>
            ) : (
              <>
                <QrCode className="h-5 w-5" aria-hidden="true" />
                <span>{t('production.filmStorageQr.printButton')}</span>
              </>
            )}
          </Button>
        </QRPreviewDialog>
      </div>

      {/* Info */}
      <div className="bg-muted/50 p-4 rounded-lg">
        <div className="flex items-start gap-2">
          <Info className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
          <div className="text-sm text-muted-foreground space-y-1">
            <p>
              {t('production.filmStorageQr.infoFormat')}{' '}
              <code className="bg-background px-1 rounded">THP://FILMPH:FILMPH-001</code>
            </p>
            <p>{t('production.filmStorageQr.infoTip')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
